import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Check, X, ListTodo, FileText, CheckCircle2 } from "lucide-react";
import {
  PLATFORMS, CAMPAIGN_STATUSES, CAMPAIGN_TYPES, POST_STATUSES,
  getPlatformColor, getPlatformLabel
} from "./constants";

interface Props {
  campaignId: string;
  onBack: () => void;
}

interface Task {
  id: string;
  title: string;
  description?: string;
  priority: number;
  completed: boolean;
  due_date?: string;
  assignee_name?: string;
}

interface Post {
  id: string;
  platform: string;
  content: string;
  status: string;
  content_type?: string;
  state_code?: string;
  created_at: string;
}

export function CampaignDetail({ campaignId, onBack }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: campaign } = useQuery({
    queryKey: ['campaign', campaignId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('social_campaigns' as any)
        .select('*')
        .eq('id', campaignId)
        .single();
      if (error) throw error;
      return data as any;
    },
  });

  const { data: tasks = [], refetch: refetchTasks } = useQuery({
    queryKey: ['campaign_tasks', campaignId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tasks')
        .select('id, title, description, priority, completed, due_date, assignee_name')
        .eq('campaign_id', campaignId)
        .order('priority', { ascending: true });
      if (error) throw error;
      return (data ?? []) as Task[];
    },
  });

  const { data: posts = [], refetch: refetchPosts } = useQuery({
    queryKey: ['campaign_posts', campaignId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('social_posts' as any)
        .select('id, platform, content, status, content_type, state_code, created_at')
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return ((data ?? []) as unknown) as Post[];
    },
  });

  const toggleTask = useMutation({
    mutationFn: async (task: Task) => {
      const { error } = await supabase
        .from('tasks')
        .update({
          completed: !task.completed,
          completed_date: !task.completed ? new Date().toISOString() : null,
        })
        .eq('id', task.id);
      if (error) throw error;
    },
    onSuccess: () => {
      refetchTasks();
      queryClient.invalidateQueries({ queryKey: ['campaign_task_stats'] });
      toast({ title: 'Task updated' });
    },
    onError: (e: Error) => toast({ title: 'Update failed', description: e.message, variant: 'destructive' }),
  });

  const setPostStatus = useMutation({
    mutationFn: async ({ postId, status }: { postId: string; status: string }) => {
      const updates: any = { status, updated_at: new Date().toISOString() };
      if (status === 'approved') updates.approved_at = new Date().toISOString();
      const { error } = await supabase
        .from('social_posts' as any)
        .update(updates)
        .eq('id', postId);
      if (error) throw error;
    },
    onSuccess: () => {
      refetchPosts();
      queryClient.invalidateQueries({ queryKey: ['social_pending_count'] });
      queryClient.invalidateQueries({ queryKey: ['approval_queue'] });
      toast({ title: 'Status updated' });
    },
    onError: (e: Error) => toast({ title: 'Update failed', description: e.message, variant: 'destructive' }),
  });

  if (!campaign) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Campaigns
        </Button>
        <p className="text-muted-foreground">Loading campaign...</p>
      </div>
    );
  }

  const taskTotal = tasks.length;
  const taskDone = tasks.filter(t => t.completed).length;
  const taskPct = taskTotal > 0 ? Math.round((taskDone / taskTotal) * 100) : 0;

  const postPending = posts.filter(p => p.status === 'pending_approval').length;
  const postApproved = posts.filter(p => p.status === 'approved').length;
  const postPosted = posts.filter(p => p.status === 'posted').length;

  const statusCfg = CAMPAIGN_STATUSES.find(s => s.id === campaign.status);
  const typeCfg = CAMPAIGN_TYPES.find(t => t.id === campaign.campaign_type);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} size="sm">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Campaigns
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <CardTitle className="text-xl">{campaign.name}</CardTitle>
              {campaign.description && (
                <CardDescription className="text-sm">{campaign.description}</CardDescription>
              )}
              <div className="flex flex-wrap gap-2 mt-2">
                {statusCfg && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg.color}`}>
                    {statusCfg.label}
                  </span>
                )}
                {typeCfg && <Badge variant="outline" className="text-xs">{typeCfg.label}</Badge>}
                {(campaign.platforms ?? []).map((p: string) => (
                  <span
                    key={p}
                    className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: `${getPlatformColor(p)}20`, color: getPlatformColor(p) }}
                  >
                    {getPlatformLabel(p)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card><CardContent className="pt-4 pb-3">
              <div className="text-xs text-muted-foreground">Tasks</div>
              <div className="text-2xl font-bold">{taskDone}/{taskTotal}</div>
              <div className={`text-xs font-medium ${taskPct === 100 ? 'text-green-600' : taskPct >= 50 ? 'text-blue-600' : 'text-gray-600'}`}>
                {taskPct}% complete
              </div>
            </CardContent></Card>

            <Card><CardContent className="pt-4 pb-3">
              <div className="text-xs text-muted-foreground">Posts pending</div>
              <div className="text-2xl font-bold text-yellow-600">{postPending}</div>
            </CardContent></Card>

            <Card><CardContent className="pt-4 pb-3">
              <div className="text-xs text-muted-foreground">Posts approved</div>
              <div className="text-2xl font-bold text-green-600">{postApproved}</div>
            </CardContent></Card>

            <Card><CardContent className="pt-4 pb-3">
              <div className="text-xs text-muted-foreground">Posts published</div>
              <div className="text-2xl font-bold text-blue-600">{postPosted}</div>
            </CardContent></Card>
          </div>

          {campaign.notes && (
            <div className="mt-4 p-3 bg-muted/50 rounded-md text-xs">
              <div className="font-semibold mb-1">Campaign notes</div>
              <div className="text-muted-foreground whitespace-pre-wrap">{campaign.notes}</div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tasks panel */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ListTodo className="w-4 h-4" />
            Tasks ({taskDone}/{taskTotal})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {tasks.length === 0 ? (
            <p className="text-sm text-muted-foreground">No tasks linked to this campaign yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead className="w-20">Priority</TableHead>
                  <TableHead className="w-32">Assignee</TableHead>
                  <TableHead className="w-28">Due</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map(t => (
                  <TableRow key={t.id} className={t.completed ? 'opacity-60' : ''}>
                    <TableCell>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => toggleTask.mutate(t)}
                        disabled={toggleTask.isPending}
                      >
                        {t.completed
                          ? <CheckCircle2 className="w-4 h-4 text-green-600" />
                          : <div className="w-4 h-4 border border-muted-foreground rounded-sm" />}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <div className={`text-sm ${t.completed ? 'line-through text-muted-foreground' : ''}`}>
                        {t.title}
                      </div>
                      {t.description && (
                        <div className="text-xs text-muted-foreground mt-0.5 max-w-xl">
                          {t.description.slice(0, 140)}{t.description.length > 140 ? '…' : ''}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={t.priority <= 10 ? 'destructive' : t.priority <= 50 ? 'default' : 'secondary'} className="text-xs">
                        {t.priority}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">{t.assignee_name ?? '—'}</TableCell>
                    <TableCell className="text-xs">{t.due_date ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Posts panel */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Posts ({posts.length})
          </CardTitle>
          <CardDescription className="text-xs">
            {postPending} pending · {postApproved} approved · {postPosted} published
          </CardDescription>
        </CardHeader>
        <CardContent>
          {posts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No posts linked to this campaign yet.</p>
          ) : (
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {posts.map(p => {
                const platformColor = getPlatformColor(p.platform);
                const statusCfg = POST_STATUSES.find(s => s.id === p.status);
                return (
                  <div key={p.id} className="border rounded-md p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span
                          className="text-xs font-semibold px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: `${platformColor}20`, color: platformColor }}
                        >
                          {getPlatformLabel(p.platform)}
                        </span>
                        {p.content_type && (
                          <Badge variant="outline" className="text-xs">{p.content_type}</Badge>
                        )}
                        {p.state_code && (
                          <Badge variant="outline" className="text-xs">{p.state_code}</Badge>
                        )}
                        {statusCfg && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${statusCfg.color}`}>
                            {statusCfg.label}
                          </span>
                        )}
                      </div>
                      {p.status === 'pending_approval' && (
                        <div className="flex gap-1">
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => setPostStatus.mutate({ postId: p.id, status: 'approved' })}
                            disabled={setPostStatus.isPending}
                          >
                            <Check className="w-3.5 h-3.5 text-green-600" />
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => setPostStatus.mutate({ postId: p.id, status: 'denied' })}
                            disabled={setPostStatus.isPending}
                          >
                            <X className="w-3.5 h-3.5 text-red-500" />
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="text-xs whitespace-pre-wrap text-foreground/90">
                      {p.content.length > 400 ? p.content.slice(0, 400) + '…' : p.content}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
